// Diagnostic streams: one case template, several parallel diagnosis variants.
//
// A case carries a shared clinical skeleton (the vignette, the history/exam
// findings everybody gets) plus an optional `streams` array. Each stream is a
// *diff* against that skeleton, the same patient, taken down a different
// diagnostic path (pre-renal vs intrinsic vs post-renal AKI, say), with its
// own investigations, expert answers and final diagnosis.
//
// Merge rules, per finding/answer array:
//   • an entry whose key matches a skeleton entry SHALLOW-MERGES over it
//     (so a stream can change just `detail` and keep aliases/category)
//   • an unmatched entry is APPENDED
//   • `omit: [key, …]` drops skeleton entries the variant shouldn't have
// The key is `entry.key` if present, else its display text, so authors only
// need explicit keys when the wording itself changes between variants.
//
// `resolveCase(caseDef, streamId)` returns a plain, stream-free case
// definition; everything downstream (loadCase, the boards, the reveals) is
// unchanged and never has to know streams exist.

import { normalize } from './matching';

// Arrays a stream may override. Order matters only for the editor's tabs.
export const STREAM_ARRAYS = ['history', 'physical', 'labs', 'diffAnswer', 'cdmAnswer'];

// diffAnswer rows are identified by their etiological class, cdmAnswer/finding
// rows by their text, unless the author pinned an explicit `key`.
export const entryKey = (entry) =>
  normalize(entry?.key || entry?.text || entry?.etiologicalClass || '');

export const getStreams = (caseDef) =>
  (Array.isArray(caseDef?.streams) ? caseDef.streams : []).filter((s) => s && s.id);

export const hasStreams = (caseDef) => getStreams(caseDef).length > 0;

export const findStream = (caseDef, streamId) =>
  getStreams(caseDef).find((s) => s.id === streamId) || null;

/**
 * `ranked` is for the answer arrays whose ORDER is itself the answer
 * (diffAnswer, cdmAnswer): there, a variant's list is its ranking, so the
 * entries it mentions lead, in its order, and the rest of the skeleton
 * follows. Findings keep their positions instead, so overriding one lab
 * doesn't shuffle the board.
 */
export function mergeEntries(base = [], overrides = [], omit = [], { ranked = false } = {}) {
  const omitted = new Set((omit || []).map((k) => normalize(k)));
  const out = (base || []).filter((e) => !omitted.has(entryKey(e))).map((e) => ({ ...e }));
  const touched = [];
  (overrides || []).forEach((entry) => {
    const key = entryKey(entry);
    const at = key ? out.findIndex((e) => entryKey(e) === key) : -1;
    if (at >= 0) out[at] = { ...out[at], ...entry };
    else out.push({ ...entry });
    touched.push(key || entryKey(entry));
  });
  if (!ranked || !touched.length) return out;
  const lead = touched
    .map((key) => out.find((e) => entryKey(e) === key))
    .filter(Boolean);
  return [...lead, ...out.filter((e) => !lead.includes(e))];
}

/**
 * Flattens a case + one of its streams into a single runnable case definition.
 * Passing no (or an unknown) stream id returns the shared skeleton, so a case
 * with streams is still perfectly runnable on its own.
 */
export function resolveCase(caseDef, streamId) {
  const { streams, ...base } = caseDef || {};
  const stream = findStream(caseDef, streamId);
  if (!stream) return base;
  const out = { ...base };
  const RANKED = ['diffAnswer', 'cdmAnswer'];
  STREAM_ARRAYS.forEach((k) => {
    out[k] = mergeEntries(base[k], stream[k], stream.omit, { ranked: RANKED.includes(k) });
  });
  if (stream.presentingSymptom) out.presentingSymptom = stream.presentingSymptom;
  if (stream.demographics) out.demographics = stream.demographics;
  if (stream.diagnosis) out.diagnosis = { ...(base.diagnosis || {}), ...stream.diagnosis };
  // Per-step statements override individually, so a variant that only changes
  // the investigations rewrites Labs and CDM and inherits the rest.
  if (stream.problemRepresentation) {
    out.problemRepresentation = { ...(base.problemRepresentation || {}), ...stream.problemRepresentation };
  }
  return out;
}

/**
 * The stream metadata stored on caseFlow so every client can show which
 * variant is running, and the facilitator can walk a queue of them in one
 * lecture. `queue` is a list of stream ids; `index` is the position in it.
 */
export function streamInfo(caseDef, streamId, queue = [], index = 0) {
  const stream = findStream(caseDef, streamId);
  if (!stream) return null;
  return {
    id: stream.id,
    label: stream.label || stream.id,
    summary: stream.summary || '',
    queue: (queue || []).filter(Boolean),
    index,
  };
}

// The next stream id in a running queue, or null when the lecture is done.
export const nextStreamId = (info) => {
  const queue = info?.queue || [];
  const at = (info?.index ?? 0) + 1;
  return at < queue.length ? queue[at] : null;
};
